import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';

const DISMISS_KEY = 'sunsetBannerDismissed_solutionPlay';

const SunsetBanner = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMyContentPage = location.pathname === '/my-content';
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === 'true');
  const [legacyAssets, setLegacyAssets] = useState(null);
  const [showInfo, setShowInfo] = useState(false);

  const isEligible = user?.roles?.some(r => r === 'content_owner' || r === 'report_viewer');
  const canManageContent = user?.roles?.some(r => r === 'content_owner' || r === 'admin');

  useEffect(() => {
    if (!isEligible || !canManageContent || dismissed) return;
    const fetchLegacyAssets = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assets?mine=true&limit=500`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        setLegacyAssets((data.assets || []).filter(a => a.solution_play));
      } catch (error) {
        setLegacyAssets([]);
      }
    };
    fetchLegacyAssets();
  }, [isEligible, dismissed]);

  if (!isEligible || dismissed) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, 'true');
    setDismissed(true);
  };

  const legacyCount = legacyAssets?.length || 0;
  const hasLegacyAssets = legacyCount > 0;
  const isUploadPage = location.pathname === '/upload';

  const renderPopoverContent = () => {
    if (legacyAssets === null) {
      return <span>Loading your asset details...</span>;
    }

    if (isUploadPage) {
      return (
        <>
          <div style={{ fontWeight: 600, marginBottom: '6px' }}>Updating an existing asset?</div>
          <div style={{ color: '#D1D5DB' }}>
            Select "Replacement" below and choose the asset you want to update — its Solution Play tag will have to be replaced with the new Conversation (Enterprise & SMC) / Conversation (SMB) tags.
          </div>
        </>
      );
    }

    if (!hasLegacyAssets) {
      return (
        <span>
          You have no assets using the legacy "Solution Play" tag. For new uploads, use the "Conversation (Enterprise & SMC)" / "Conversation (SMB)" fields instead.
        </span>
      );
    }

    if (isMyContentPage) {
      return (
        <>
          <div style={{ fontWeight: 600, marginBottom: '8px' }}>
            Assets to update ({legacyCount}):
          </div>
          <div style={{ marginBottom: '8px' }}>
            {legacyAssets.map(a => (
              <div key={a.id} style={{ display: 'flex', gap: '6px', marginBottom: '2px' }}>
                <span>•</span>
                <span>{a.title}</span>
              </div>
            ))}
          </div>
          <div style={{ color: '#D1D5DB' }}>
            Open Upload Content and select "Replacement" for each to switch it to the new Conversation tags.
          </div>
        </>
      );
    }

    return (
      <>
        <div style={{ fontWeight: 600, marginBottom: '6px' }}>
          {legacyCount} asset{legacyCount === 1 ? '' : 's'} still use "Solution Play"
        </div>
        <div style={{ color: '#D1D5DB' }}>
          Go to My Content to see which assets need updating.
        </div>
      </>
    );
  };

  return (
    <div
      data-testid="sunset-banner"
      style={{
        backgroundColor: '#FFFBEB',
        borderBottom: '1px solid #FCD34D',
        padding: '12px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        flexWrap: 'wrap'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', justifyContent: 'center' }}>
        <AlertTriangle size={18} color="#B45309" style={{ flexShrink: 0 }} />
        <span style={{ color: '#92400E', fontSize: '14px' }}>
          {canManageContent ? (
            <>
              <strong>"FY26 Solution Plays" are being sunset.</strong> Please update your existing assets to be linked with new "FY27 Conversations" and stay aligned with the latest guidelines.
            </>
          ) : (
            <>
              <strong>"Solution Play" is being sunset.</strong> This is a platform-wide change. Content tagging is moving from "Solution Play" to "Conversation (Enterprise & SMC)" / "Conversation (SMB)".
            </>
          )}
        </span>
        {canManageContent && (
        <div
          style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
          onMouseEnter={() => setShowInfo(true)}
          onMouseLeave={() => setShowInfo(false)}
        >
          <span
            data-testid="sunset-banner-info-icon"
            style={{
              color: '#92400E',
              fontSize: '13px',
              fontWeight: '600',
              textDecoration: 'underline',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            Learn More ▾
          </span>
          {showInfo && (
            <div
              data-testid="sunset-banner-info-popover"
              style={{
                position: 'absolute',
                top: '24px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#1F2937',
                color: '#F9FAFB',
                borderRadius: '8px',
                padding: '12px 14px',
                fontSize: '12px',
                width: '280px',
                boxShadow: '0 8px 20px rgba(0,0,0,0.25)',
                zIndex: 50,
                textAlign: 'left'
              }}
            >
              {renderPopoverContent()}
            </div>
          )}
        </div>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {canManageContent && hasLegacyAssets && (
          <button
            onClick={() => navigate('/my-content')}
            data-testid="sunset-banner-cta"
            style={{
              backgroundColor: '#1B5E5F',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Review &amp; Update My Content
          </button>
        )}
        {canManageContent && (
        <button
          onClick={() => navigate('/upload')}
          data-testid="sunset-banner-upload-cta"
          style={{
            backgroundColor: '#1B5E5F',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '6px',
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          Upload Content
        </button>
        )}
        <button
          onClick={handleDismiss}
          data-testid="sunset-banner-dismiss"
          title="Dismiss"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            color: '#92400E',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};

export default SunsetBanner;
